import { gql, useMutation, useQuery } from '@apollo/client';
import { EmailStatusEnumType, FundDealConsentInviteType } from '__generated__/graphql';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

const FUND_DEAL_CONSENT_INVITE_QUERY = gql`
  query FundDealConsentInvites($fundId: String!, $dealId: String!) {
    fundDealConsentInvites(fundId: $fundId, dealId: $dealId) {
      emailStatus
      createdAt
      fundClosing {
        investmentEntity {
          name
          user {
            email
          }
        }
      }
    }
  }
`;

const CREATE_FUND_DEAL_CONSENT_INVITE_MUTATION = gql`
  mutation CreateFundDealConsentInvite($fundId: ID!, $dealId: ID!) {
    createFundDealConsentInvites(fundId: $fundId, dealId: $dealId, triggerEmail: true) {
      total
      successful
      failed
      skipped
    }
  }
`;

const ConsentInvite = () => {
  const { dealId, fundId } = useParams<{ dealId: string; fundId: string }>();

  const { showToast, handleToastOpen, handleToastClose } = useToast();

  const { loading, error, data, refetch } = useQuery(FUND_DEAL_CONSENT_INVITE_QUERY, {
    variables: {
      fundId,
      dealId,
    },
  });

  const [createFundDealConsentInvite, { loading: createFundDealConsentInviteLoading }] =
    useMutation(CREATE_FUND_DEAL_CONSENT_INVITE_MUTATION);

  if (loading) return <LoadingIndicator />;

  if (error) return <ErrorMessage error={error} refetch={refetch} />;

  const { fundDealConsentInvites }: { fundDealConsentInvites: FundDealConsentInviteType[] } = data;

  const showSendInvite =
    fundDealConsentInvites.length === 0 ||
    fundDealConsentInvites.filter(invite => invite.emailStatus === EmailStatusEnumType.Pending)
      .length > 0;

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center">
        <h1 className="text-lg font-bold mb-4">Consent Invites</h1>
        {showSendInvite && (
          <Button
            loading={createFundDealConsentInviteLoading}
            variant="primary"
            onClick={() =>
              createFundDealConsentInvite({ variables: { fundId, dealId } }).then(() => {
                refetch();
                handleToastOpen();
              })
            }
          >
            Send Invites
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <DataTable
          data={fundDealConsentInvites}
          columns={[
            {
              label: 'Investment Entity',
              fieldName: 'fundClosing.investmentEntity.name',
              type: 'STRING',
            },
            {
              label: 'Email',
              fieldName: 'fundClosing.investmentEntity.user.email',
              type: 'STRING',
            },
            {
              label: 'Delivered At',
              fieldName: 'createdAt',
              type: 'DATE',
            },
            {
              label: 'Email Status',
              fieldName: 'emailStatus',
              type: 'EMAIL_STATUS',
            },
          ]}
        />
      </div>

      <Toast
        type="success"
        title="Invites Sent"
        message={
          'We have sent invites to the investment entities. They will be notified via email.'
        }
        show={showToast}
        handleClose={handleToastClose}
      />
    </div>
  );
};

export default ConsentInvite;
